import * as Yup from "yup";
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { createNewAccount, getCreateAccOptions } from "../../services/services";
import { trimValues } from "../../utils/appUtils";
import FormDialog from "../../components/FormDialog";

const CreateNewAccount = ({ open, onClose }) => {
  const [options, setOptions] = useState();
  const [optLoaded, setOptLoaded] = useState(false);
  const handleSubmit = async (values, { setSubmitting }) => {
    const newValues = trimValues(values);
    const {
      email,
      salutation,
      firstName,
      lastName,
      contactNumber,
      group,
      entity,
      department,
      role,
    } = newValues;
    await createNewAccount(
      email,
      firstName,
      lastName,
      contactNumber,
      department,
      group,
      entity,
      salutation,
      onClose
    );
    setSubmitting(false);
  };

  useEffect(() => {
    getCreateAccOptions(setOptions, setOptLoaded);
  }, []);

  const entityOptions = (options?.oxygen7_entity || []).filter(
    (entity) =>
      entity?.entity !== "OXYGEN7_IND" &&
      entity?.entity !== "OXYGEN7_HKG"
  );

  const defaultEntity =
    entityOptions.find(
      (entity) => entity?.entity?.toUpperCase() === "OXYGEN7_BT"
    ) || (entityOptions.length === 1 ? entityOptions[0] : null);

  return (
    <Container>
      {optLoaded ? (
        <Box>
          <Formik
            initialValues={{
              email: "",
              salutation: "",
              firstName: "",
              lastName: "",
              contactNumber: "",
              group: "",
              entity: defaultEntity?.id ?? "",
              department: "",
            }}
            enableReinitialize
            onSubmit={handleSubmit}
            validationSchema={Yup.object().shape({
              email: Yup.string().email("Invalid email").required("Required"),
              salutation: Yup.string().required("Salutation is required"),
              firstName: Yup.string().required("First name is required").trim(),
              lastName: Yup.string().required("Last name is required").trim(),
              contactNumber: Yup.string().required(
                "Contact number is required"
              ).trim(),
              group: Yup.string().required("Group is required"),
              entity: Yup.string(),
              department: Yup.string().required("Department is required"),
            })}
          >
            {(props) => {
              const {
                values,
                touched,
                errors,
                isSubmitting,
                handleChange,
                handleBlur,
                handleSubmit,
              } = props;
              return (
                <FormDialog
                  open={open}
                  onClose={onClose}
                  title="Create A New Account"
                  scroll="paper"
                  handleSubmit={handleSubmit}
                  loading={isSubmitting}
                >
                  <Form autoComplete="off" noValidate>
                    <Grid
                      container
                      rowSpacing={2}
                      columnSpacing={3}
                      sx={{ alignItems: "center", py: 2, px: 2 }}
                    >
                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          Email*
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="email"
                          name="email"
                          label="Email"
                          size="small"
                          color="secondary"
                          value={values.email}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                          disabled={isSubmitting}
                        />
                      </Grid>

                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          Salutation*
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          required
                          select
                          id="salutation"
                          name="salutation"
                          size="small"
                          color="secondary"
                          fullWidth
                          label="Salutation"
                          value={values.salutation}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.salutation && errors.salutation)}
                          helperText={touched.salutation && errors.salutation}
                          disabled={isSubmitting}
                        >
                          {options?.salutation?.map((sal) => (
                            <MenuItem key={sal.id} value={sal.id}>
                              {sal.salutation}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          First Name*
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="firstName"
                          name="firstName"
                          label="First Name"
                          size="small"
                          color="secondary"
                          value={values.firstName}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.firstName && errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                          disabled={isSubmitting}
                        />
                      </Grid>

                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          Last Name*
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="lastName"
                          name="lastName"
                          label="Last Name"
                          size="small"
                          color="secondary"
                          value={values.lastName}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.lastName && errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                          disabled={isSubmitting}
                        />
                      </Grid>

                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          Contact Number*
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="contactNumber"
                          name="contactNumber"
                          label="Contact Number"
                          size="small"
                          color="secondary"
                          value={values.contactNumber}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched.contactNumber && errors.contactNumber
                          )}
                          helperText={
                            touched.contactNumber && errors.contactNumber
                          }
                          disabled={isSubmitting}
                        />
                      </Grid>

                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          Entity
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          select
                          id="entity"
                          name="entity"
                          label="Entity"
                          size="small"
                          color="secondary"
                          value={values.entity}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.entity && errors.entity)}
                          helperText={touched.entity && errors.entity}
                          disabled={isSubmitting}
                          fullWidth
                        >
                          {entityOptions.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.entity}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          Group*
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          required
                          select
                          id="group"
                          name="group"
                          label="Group"
                          size="small"
                          color="secondary"
                          value={values.group}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.group && errors.group)}
                          helperText={touched.group && errors.group}
                          disabled={isSubmitting}
                          fullWidth
                        >
                          {options?.group_name?.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item md={2} xs={2}>
                        <Typography
                          variant="subtitle1"
                          sx={{ textAlign: "right" }}
                        >
                          Department*
                        </Typography>
                      </Grid>

                      <Grid item md={4} xs={4}>
                        <TextField
                          margin="normal"
                          required
                          select
                          id="department"
                          name="department"
                          label="Department"
                          size="small"
                          color="secondary"
                          value={values.department}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={Boolean(touched.department && errors.department)}
                          helperText={touched.department && errors.department}
                          disabled={isSubmitting}
                          fullWidth
                        >
                          {options?.department?.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.department}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Form>
                </FormDialog>
              );
            }}
          </Formik>
        </Box>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Container>
  );
};

export default CreateNewAccount;
